import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft, Plug, CheckCircle2, AlertTriangle, XCircle, Loader2,
} from "lucide-react";

/**
 * Admin → Integrations → Providers.
 * Shows every provider VAULT can support with its market approval state,
 * connection count, error rate, and env-var readiness.
 */
const STATUS_COLOR = {
    "Research":         "bg-stone-100 text-stone-700",
    "Provider Review":  "bg-stone-100 text-stone-700",
    "Security Review":  "bg-amber-50 text-amber-700",
    "Privacy Review":   "bg-amber-50 text-amber-700",
    "Legal Review":     "bg-amber-50 text-amber-700",
    "Approved":         "bg-blue-50 text-blue-700",
    "Beta":             "bg-indigo-50 text-indigo-700",
    "Live":             "bg-emerald-50 text-emerald-700",
    "Blocked":          "bg-rose-50 text-rose-700",
};

export default function IntegrationsAdmin() {
    const [data, setData] = useState(null);

    const load = async () => {
        try { setData((await api.get("/connect/admin/providers")).data); }
        catch { toast.error("Failed to load providers"); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (provider, status) => {
        try {
            await api.post("/connect/admin/market-approval", { provider, status, country: "*" });
            toast.success(`${provider} → ${status}`);
            await load();
        } catch { toast.error("Update failed"); }
    };

    if (!data) return <div className="p-8"><Loader2 size={16} className="animate-spin" /></div>;

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="integrations-admin">
            <div className="mb-4 text-xs">
                <Link to="/super-admin" className="text-stone-500 hover:text-stone-900 flex items-center gap-1">
                    <ArrowLeft size={12} /> Super Admin
                </Link>
            </div>
            <div className="flex items-start gap-3 mb-6">
                <div className="mt-1 rounded-full bg-indigo-50 p-1.5">
                    <Plug size={16} className="text-indigo-600" />
                </div>
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Integrations · Providers</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-2xl">
                        Regional approval matrix for every financial-data connectivity provider VAULT can route through. Providers move from Research → Legal Review → Live as security, privacy and compliance sign off.
                    </p>
                </div>
            </div>

            <div className="rounded-xl border border-stone-200 bg-white overflow-x-auto">
                <table className="min-w-full text-xs" data-testid="provider-table">
                    <thead className="bg-stone-50 text-stone-500 uppercase tracking-wide text-[10px]">
                        <tr>
                            <th className="text-left px-3 py-2">Provider</th>
                            <th className="text-left px-3 py-2">Region</th>
                            <th className="text-left px-3 py-2">Products</th>
                            <th className="text-left px-3 py-2">Env</th>
                            <th className="text-left px-3 py-2">Connections</th>
                            <th className="text-left px-3 py-2">Success</th>
                            <th className="text-left px-3 py-2">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {data.providers.map((p) => (
                            <tr key={p.id} data-testid={`row-${p.id}`}>
                                <td className="px-3 py-2 font-medium">{p.name}</td>
                                <td className="px-3 py-2">{(p.region || []).join(", ")}</td>
                                <td className="px-3 py-2">{(p.products || []).join(", ")}</td>
                                <td className="px-3 py-2">
                                    {p.env_ready
                                        ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 size={11} /> Ready</span>
                                        : <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={11} /> Missing keys</span>}
                                </td>
                                <td className="px-3 py-2">{p.connections}</td>
                                <td className="px-3 py-2">{p.success_rate == null ? "—" : `${p.success_rate}%`}</td>
                                <td className="px-3 py-2">
                                    <Select value={p.market_status || "Research"} onValueChange={(v) => setStatus(p.id, v)}>
                                        <SelectTrigger className={`h-7 text-xs ${STATUS_COLOR[p.market_status || "Research"]}`}
                                            data-testid={`status-${p.id}`}>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {data.statuses.map((s) => (
                                                <SelectItem key={s} value={s}>{s}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4 text-[11px] text-stone-500">
                Providers stay OFF for users until their market approval reads <b>Live</b> AND the <code>vault_connect</code> feature flag is enabled for the workspace.
            </div>
        </div>
    );
}
